import { memo, type ReactNode, useCallback } from 'react';
import { useFocusRing, useHover } from 'react-aria';
import cn from 'classnames';
import { type TestProps } from '@direct-frontend/components/testing';
import { useRadioState } from '../../../../lib/hooks';
import { makeModifierClassname } from '../../../../lib/makeModifierClassname';
import { RadioGroupContext } from '../../lib/RadioGroupContext';
import styles from './ChipRadio.module.css';

const modifier = makeModifierClassname(styles, 'chip');

export interface ChipRadioProps extends TestProps {
    children?: ReactNode;
    value: string;
    isDisabled?: boolean;
    isDashed?: boolean;
    className?: string;
    title?: string;
    'aria-label'?: string;
}

export const ChipRadio = memo(function ChipRadioProps(props: ChipRadioProps) {
    const {
        'data-testid': dataTestId = 'ChipRadio',
        children,
        value,
        isDisabled = false,
        isDashed = false,
        className,
        title,
        'aria-label': ariaLabel,
    } = props;

    const { selectedValue, setSelectedValue, name } = useRadioState(RadioGroupContext);

    const isSelected = selectedValue === value;

    const handleChange = useCallback(() => {
        if (!isDisabled) {
            setSelectedValue?.(value);
        }
    }, [isDisabled, value, setSelectedValue]);

    const { hoverProps, isHovered } = useHover({ ...props, isDisabled });
    const { focusProps, isFocused } = useFocusRing({ ...props, isTextInput: true });

    return (
        <label
            {...hoverProps}
            {...focusProps}
            title={title ?? value ?? ariaLabel}
            className={cn(
                className,
                modifier.className,
                modifier('selected', isSelected),
                modifier('dashed', isDashed && !isDisabled && !isSelected),
                {
                    [styles.hovered]: isHovered,
                    [styles.focused]: isFocused,
                    [styles.disabled]: isDisabled,
                }
            )}
            data-testid={dataTestId}
        >
            <input
                type="radio"
                name={name}
                value={value}
                checked={isSelected}
                disabled={isDisabled}
                className={styles.input}
                aria-label={ariaLabel || `Выбрать ${value}`}
                onChange={handleChange}
                data-testid={`${dataTestId}.Input`}
            />
            {children}
        </label>
    );
});
